import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEOHead from '../components/SEOHead';
import HeroParticles from '../components/HeroParticles';
import logo from '../assets/images/logo.webp';
import '../styles/error-pages.css';

const KundenWartungPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const customer = searchParams.get('customer');
  const customerLogo = searchParams.get('customerLogo');
  const supportMail = searchParams.get('supportMail') || 'info@stimm.digital';

  // Verstecke Chatwoot auf dieser Seite
  useEffect(() => {
    const hideChatwoot = () => {
      if (window.$chatwoot) {
        window.$chatwoot.toggle('close');
        const chatwootWidget = document.querySelector('.woot-widget-bubble');
        if (chatwootWidget) {
          (chatwootWidget as HTMLElement).style.display = 'none';
        }
      }
    };
    
    // Versuche es sofort und nach kurzer Verzögerung, falls es noch lädt
    hideChatwoot();
    const timeoutId = setTimeout(hideChatwoot, 1000);
    
    return () => {
      clearTimeout(timeoutId);
      // Chatwoot beim Verlassen der Seite wieder einblenden
      const chatwootWidget = document.querySelector('.woot-widget-bubble');
      if (chatwootWidget) {
        (chatwootWidget as HTMLElement).style.display = 'flex';
      }
    };
  }, []);

  return (
    <>
      <SEOHead
        title="Wartungsarbeiten"
        description="Diese Anwendung befindet sich derzeit in Wartung."
        keywords="Wartung, Maintenance"
      />
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}>
        {/* Background Effects */}
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0, opacity: 0.5 }}>
          <HeroParticles />
        </div>
        
        {/* Glow Effects */}
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '60vw',
          height: '60vw',
          background: 'radial-gradient(circle, rgba(74, 222, 128, 0.05) 0%, rgba(0,0,0,0) 70%)',
          zIndex: 0,
          pointerEvents: 'none'
        }} />

        <div style={{ position: 'relative', zIndex: 10, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>

          {/* Heading */}
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
            style={{
              marginTop: '4rem',
              fontSize: 'clamp(2.5rem, 6vw, 4rem)',
              fontWeight: '800',
              fontFamily: "'Satoshi', sans-serif",
              color: '#ffffff',
              marginBottom: '3rem',
              lineHeight: 1.2,
              letterSpacing: '-0.02em',
              paddingBottom: '0.2em'
            }}
          >
            Wir sind gleich wieder da
          </motion.h1>

          {/* Customer message - Glassmorphism */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3, ease: "easeOut" }}
            style={{
              maxWidth: '600px',
              padding: '3rem',
              background: 'rgba(255, 255, 255, 0.02)',
              border: '1px solid rgba(255, 255, 255, 0.05)',
              borderRadius: '1.5rem',
              backdropFilter: 'blur(20px)',
              boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
              marginBottom: '3rem',
              position: 'relative',
              overflow: 'hidden',
              width: '100%'
            }}
          >
            {/* Subtle top highlight */}
            <div style={{ position: 'absolute', top: 0, left: '10%', right: '10%', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(74,222,128,0.3), transparent)' }} />
            
            <p style={{
              fontSize: '1.25rem',
              color: 'var(--color-gray, #e4e4e7)',
              lineHeight: 1.6,
              margin: 0,
              fontWeight: 300
            }}>
              Wir führen aktuell ein Update durch, um dir das bestmögliche Erlebnis zu bieten.
              
              {(customer || customerLogo) && (
                <>
                  <br />
                  <span style={{ opacity: 0.7, fontSize: '1.1rem', marginTop: '1.5rem', display: 'inline-block' }}>
                    Im Auftrag von
                  </span>
                  <br />
                  {customerLogo ? (
                    <img 
                      src={customerLogo} 
                      alt={customer || 'Kundenlogo'} 
                      style={{ 
                        maxHeight: '40px', 
                        maxWidth: '200px', 
                        marginTop: '1rem',
                        objectFit: 'contain',
                        display: 'block',
                        margin: '1rem auto 0 auto'
                      }} 
                    />
                  ) : (
                    <strong style={{
                      color: '#ffffff',
                      fontFamily: "'Satoshi', sans-serif",
                      fontWeight: 700,
                      fontSize: '1.75rem',
                      display: 'inline-block',
                      marginTop: '0.25rem'
                    }}>
                      {customer}
                    </strong>
                  )}
                </>
              )}
            </p>
            <p style={{
              fontSize: '1.05rem',
              color: 'rgba(228, 228, 231, 0.6)',
              lineHeight: 1.6,
              marginTop: '1.5rem',
              marginBottom: 0
            }}>
              Bitte habe einen Moment Geduld. Wir sind in Kürze wieder vollumfänglich für dich erreichbar.
            </p>
          </motion.div>

        {/* Contact link */}
        <motion.a
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: "easeOut" }}
          href={`mailto:${supportMail}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '1rem 2rem',
            background: 'linear-gradient(135deg, var(--color-green, #4ade80), var(--color-green-hover, #22c55e))',
            color: '#000',
            fontFamily: "'Satoshi', sans-serif",
            fontWeight: 700,
            fontSize: '1rem',
            borderRadius: '3rem',
            textDecoration: 'none',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            boxShadow: '0 8px 30px rgba(74, 222, 128, 0.2)',
            zIndex: 10
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLAnchorElement).style.transform = 'translateY(-3px)';
            (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 12px 40px rgba(74, 222, 128, 0.3)';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLAnchorElement).style.transform = '';
            (e.currentTarget as HTMLAnchorElement).style.boxShadow = '0 8px 30px rgba(74, 222, 128, 0.2)';
          }}
        >
          <i className="fas fa-headset"></i>
          Support kontaktieren
        </motion.a>

        {/* Branding footer */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 0.5 }}
          transition={{ duration: 1, delay: 0.8 }}
          style={{
             marginTop: '6rem',
             display: 'flex',
             flexDirection: 'column',
             alignItems: 'center',
             gap: '0.75rem',
             filter: 'grayscale(100%)',
             transition: 'opacity 0.3s ease, filter 0.3s ease'
          }}
          onMouseEnter={e => {
             (e.currentTarget as HTMLDivElement).style.opacity = '1';
             (e.currentTarget as HTMLDivElement).style.filter = 'grayscale(0%)';
          }}
          onMouseLeave={e => {
             (e.currentTarget as HTMLDivElement).style.opacity = '0.5';
             (e.currentTarget as HTMLDivElement).style.filter = 'grayscale(100%)';
          }}
        >
          <p style={{
            fontSize: '0.75rem',
            color: 'rgba(209, 213, 219, 0.6)',
            fontFamily: "'Satoshi', sans-serif",
            textTransform: 'uppercase',
            letterSpacing: '0.1em'
          }}>
            Technologiepartner
          </p>
          <a
            href="https://stimm.digital"
            style={{ 
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
            }}
            target="_blank"
            rel="noopener noreferrer"
          >
            <img src={logo} alt="StimmDigital" style={{ height: '20px' }} />
          </a>
        </motion.div>
        
        </div>
      </div>
    </>
  );
};

export default KundenWartungPage;

